/**
 * ImportAssetsTable - A responsive table component for displaying fetched asset properties with grouping capabilities
 *
 * Features:
 * - Dynamic grouping by any property field (primary and secondary levels)
 * - Responsive layout that collapses columns on narrow screens
 * - Group interaction logging (click and toggle events)
 * - Tablesaw-style collapsed row formatting
 * - Optimized for asset property data from Autodesk Forge Viewer
 */
import React, { useRef, useEffect, useState } from "react";
import { ReactTabulator } from "react-tabulator";
import "react-tabulator/lib/styles.css";
import "react-tabulator/css/tabulator_bootstrap5.min.css";

/**
 * Custom formatter for collapsed rows - creates a Tablesaw-style table
 * @param {Array} cols - Array of column objects with title and value
 * @returns {HTMLElement} - Formatted table element
 */
const collapseFormatter = (cols) => {
    const tbl = document.createElement("table");
    tbl.className = "ts-collapse";
    cols.forEach(c => {
        const tr = document.createElement("tr");
        tr.innerHTML = `<th>${c.title}</th><td>${c.value ?? ""}</td>`;
        tbl.appendChild(tr);
    });
    return tbl;
};

/**
 * Transform raw asset properties data into table-friendly format (optimized for performance)
 * @param {Array} rawData - Raw properties data from Autodesk Forge Viewer
 * @returns {Array} - Transformed data suitable for table display
 */
const transformAssetData = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    
    // Pre-compile regex for better performance
    const sanitizeRegex = /[^a-zA-Z0-9]/g;
    
    return rawData.map((item, index) => {
        // Clean the name by removing dbId prefix if present
        let cleanName = item.name || `Object ${item.dbId || index}`;
        if (item.dbId && cleanName.startsWith(item.dbId.toString())) {
            // Remove dbId prefix and any following separator (space, dash, etc.)
            cleanName = cleanName.replace(new RegExp(`^${item.dbId}[\\s\\-_]*`), '').trim();
            // If nothing left after removing prefix, use a default name
            if (!cleanName) {
                cleanName = `Object ${item.dbId}`;
            }
        }
        
        const transformed = {
            dbId: item.dbId,
            name: cleanName,
        };
        
        // Extract properties into flat structure for table columns
        if (item.properties && Array.isArray(item.properties)) {
            // Look for a better name in properties first
            const nameProperty = item.properties.find(prop => 
                prop.displayName && (
                    prop.displayName.toLowerCase().includes('name') ||
                    prop.displayName.toLowerCase().includes('label') ||
                    prop.displayName.toLowerCase().includes('title')
                ) && 
                prop.displayValue && 
                typeof prop.displayValue === 'string' &&
                !prop.displayValue.startsWith(item.dbId?.toString())
            );
            
            if (nameProperty && nameProperty.displayValue.trim()) {
                transformed.name = nameProperty.displayValue.trim();
            }
            
            // Limit properties to avoid too many columns (performance optimization)
            const limitedProps = item.properties.slice(0, 50);
            
            limitedProps.forEach(prop => {
                if (prop.displayName && prop.displayValue !== undefined && prop.displayValue !== null) {
                    // Use displayName as key, sanitize for use as object property
                    const key = prop.displayName.replace(sanitizeRegex, '_').toLowerCase();
                    
                    // Convert values to strings for better table performance
                    const value = typeof prop.displayValue === 'object' 
                        ? JSON.stringify(prop.displayValue) 
                        : String(prop.displayValue);
                    
                    transformed[key] = value;
                    
                    // Only store metadata for important properties to reduce memory usage
                    if (prop.displayCategory && prop.displayCategory !== 'Other') {
                        transformed[`${key}_category`] = prop.displayCategory;
                    }
                }
            });
        }
        
        return transformed;
    });
};

/**
 * Generate dynamic columns based on the transformed data (optimized for performance)
 * @param {Array} data - Transformed asset data
 * @returns {Array} - Column definitions for Tabulator
 */
const generateColumns = (data) => {
    if (!data || data.length === 0) return [];
    
    // Sample first 100 rows to determine columns (performance optimization)
    const sampleData = data.slice(0, 100);
    
    // Get all unique keys from the sample data
    const allKeys = new Set();
    sampleData.forEach(item => {
        Object.keys(item).forEach(key => {
            // Skip internal keys and category/type metadata
            if (!key.endsWith('_category') && !key.endsWith('_type')) {
                allKeys.add(key);
            }
        });
    });
    
    const columns = [];
    
    // Always include basic columns first with performance optimizations
    if (allKeys.has('dbId')) {
        columns.push({ 
            title: "DB ID", 
            field: "dbId", 
            responsive: 0, 
            width: 80,
            sorter: "number",
            headerSort: true
        });
    }
    if (allKeys.has('name')) {
        columns.push({ 
            title: "Name", 
            field: "name", 
            responsive: 0, 
            width: 200,
            sorter: "string",
            headerSort: true
        });
    }
    
    // Add other property columns with responsive priorities (limit to first 20 for performance)
    const otherKeys = Array.from(allKeys).filter(key => 
        !['dbId', 'name'].includes(key)
    ).sort().slice(0, 20); // Limit to 20 additional columns for performance
    
    otherKeys.forEach((key, index) => {
        columns.push({
            title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            field: key,
            responsive: index < 3 ? 1 : index < 6 ? 2 : 3, // Progressive hiding
            width: 150,
            sorter: "string",
            headerSort: true,
            // Disable sorting for less important columns to improve performance
            headerSort: index < 10
        });
    });
    
    return columns;
};

export default function ImportAssetsTable({ data = [] }) {
    // Ref to access the Tabulator instance
    const tableRef = useRef(null);
    // State for the primary grouping field (empty string = no grouping)
    const [groupField, setGroupField] = useState("");
    // State for the secondary grouping field (empty string = no secondary grouping)
    const [secondaryGroupField, setSecondaryGroupField] = useState("");
    // Loading state for data processing
    const [isProcessing, setIsProcessing] = useState(false);
    
    // Transform and prepare data with loading state
    const [transformedData, setTransformedData] = useState([]);
    const [columns, setColumns] = useState([]);
    
    // Process data asynchronously to avoid blocking UI
    useEffect(() => {
        if (!data || data.length === 0) {
            setTransformedData([]);
            setColumns([]);
            return;
        }
        
        setIsProcessing(true);
        
        // Use setTimeout to allow UI to update before processing
        const processData = () => {
            try {
                const transformed = transformAssetData(data);
                const cols = generateColumns(transformed);
                setTransformedData(transformed);
                setColumns(cols);
            } catch (error) {
                console.error('Error processing asset data:', error);
                setTransformedData([]);
                setColumns([]);
            } finally {
                setIsProcessing(false);
            }
        };
        
        setTimeout(processData, 10);
    }, [data]);

    /**
     * Setup responsive behavior and event handlers (optimized with throttling)
     * Re-runs when grouping fields change to handle new table instances
     */
    useEffect(() => {
        const table = tableRef.current?.table;
        if (!table || columns.length === 0) return;

        // Add data attribute for grouped tables to enable specific CSS
        const tabulatorElement = table.element;
        if (tabulatorElement) {
            if (groupField) {
                tabulatorElement.setAttribute('data-grouped', 'true');
            } else {
                tabulatorElement.removeAttribute('data-grouped');
            }
        }

        // Throttle resize handler for better performance
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const narrow = window.innerWidth < 640;
                const medium = window.innerWidth < 1024;
                
                // Hide columns based on screen size and responsive priority
                columns.forEach(col => {
                    if (col.responsive === 0) return; // Always visible
                    
                    const shouldHide = narrow ? col.responsive >= 1 : 
                                     medium ? col.responsive >= 2 : 
                                     col.responsive >= 3;
                    
                    try {
                        shouldHide ? table.hideColumn(col.field) : table.showColumn(col.field);
                    } catch (error) {
                        // Ignore column visibility errors
                    }
                });
            }, 150); // 150ms throttle
        };

        // Apply initial responsive state and setup listener
        handleResize();
        window.addEventListener("resize", handleResize);

        // Cleanup function
        return () => {
            clearTimeout(resizeTimeout);
            window.removeEventListener("resize", handleResize);
        };
    }, [groupField, secondaryGroupField, columns]);

    /**
     * Recursively extract group data including nested subgroups
     * @param {Object} group - Tabulator group object
     * @returns {Object} - Structured group data with nested subgroups
     */
    const extractGroupDataRecursive = (group) => {
        const groupData = {
            key: group.getKey(),
            field: group.getField(),
            visible: group.isVisible(),
            subGroups: [],
            rows: []
        };

        // Get all child groups (subgroups)
        const subGroups = group.getSubGroups();
        if (subGroups && subGroups.length > 0) {
            groupData.subGroups = subGroups.map(subGroup => extractGroupDataRecursive(subGroup));
        }

        // Get direct row data (not in subgroups)
        const rows = group.getRows();
        if (rows && rows.length > 0) {
            groupData.rows = rows.map(row => row.getData());
        }

        return groupData;
    };

    /**
     * Handle group label clicks - logs group info to console recursively
     * @param {Event} _e - Click event (unused)
     * @param {Object} group - Tabulator group object
     */
    const handleGroupClick = (_e, group) => {
        console.log("Asset group clicked:", group.getKey());
        const recursiveGroupData = extractGroupDataRecursive(group);
        console.log("Asset group data (recursive):", JSON.stringify(recursiveGroupData, null, 2));
    };

    /**
     * Handle group expand/collapse - logs toggle state
     * @param {Object} group - Tabulator group object
     */
    const handleGroupToggle = (group) => {
        console.log("Asset group toggled:", group.getKey(), "- Visible:", group.isVisible());
    };

    /**
     * Build groupBy configuration based on selected fields
     * Supports single or multi-level grouping
     */
    const buildGroupBy = () => {
        if (!groupField) return false; // No grouping

        if (!secondaryGroupField) {
            return groupField; // Single level grouping
        }

        // Multi-level grouping - return array of fields
        return [groupField, secondaryGroupField];
    };

    // Show loading state while processing data
    if (isProcessing) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                <div>Processing {data.length} assets...</div>
                <div style={{ marginTop: '10px', fontSize: '12px' }}>
                    This may take a moment for large datasets
                </div>
            </div>
        );
    }

    // Show message if no data
    if (!transformedData || transformedData.length === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No asset data available. Click "Fetch All Properties" to load data.
            </div>
        );
    }

    // Tabulator configuration options with grouping-specific fixes
    const baseOptions = {
        layout: "fitColumns",
        responsiveLayout: "collapse",
        responsiveLayoutCollapseStartOpen: false,
        responsiveLayoutCollapseFormatter: collapseFormatter,
        headerSort: true,
        pagination: "local",
        paginationSize: 50,
        paginationSizeSelector: [25, 50, 100, 200],
    };

    // Different configuration for grouped vs non-grouped tables
    const groupingOptions = groupField ? {
        // When grouping is active, use fixed height to prevent scroll issues
        height: 400,
        maxHeight: 400,
        groupBy: buildGroupBy(),
        groupStartOpen: true, // Start with groups open so data is visible
        // Disable virtual DOM for groups to prevent scroll conflicts
        virtualDom: false,
        // Force scroll container behavior
        scrollToRowPosition: "top",
        scrollToColumnPosition: "left",
    } : {
        // When no grouping, use flexible height
        height: "100%",
        virtualDom: true,
    };

    const options = { ...baseOptions, ...groupingOptions };

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Data summary */}
            <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666', flexShrink: 0 }}>
                Showing {transformedData.length} assets
            </div>
            
            {/* Grouping control dropdowns */}
            <div style={{ margin: "0 0 15px", display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
                <div>
                    <label htmlFor="group-select" style={{ fontSize: '14px', fontWeight: '500' }}>
                        Primary Group:&nbsp;
                    </label>
                    <select
                        id="group-select"
                        value={groupField}
                        onChange={e => {
                            setGroupField(e.target.value);
                            // Reset secondary group if primary is cleared
                            if (!e.target.value) setSecondaryGroupField("");
                        }}
                        style={{ padding: '4px 8px', fontSize: '14px' }}
                    >
                        <option value="">None</option>
                        {columns.map(c => (
                            <option key={c.field} value={c.field}>{c.title}</option>
                        ))}
                    </select>
                </div>

                {/* Secondary grouping dropdown - only show if primary group is selected */}
                {groupField && (
                    <div>
                        <label htmlFor="secondary-group-select" style={{ fontSize: '14px', fontWeight: '500' }}>
                            Secondary Group:&nbsp;
                        </label>
                        <select
                            id="secondary-group-select"
                            value={secondaryGroupField}
                            onChange={e => setSecondaryGroupField(e.target.value)}
                            style={{ padding: '4px 8px', fontSize: '14px' }}
                        >
                            <option value="">None</option>
                            {columns
                                .filter(c => c.field !== groupField) // Exclude primary group field
                                .map(c => (
                                    <option key={c.field} value={c.field}>{c.title}</option>
                                ))
                            }
                        </select>
                    </div>
                )}
            </div>

            {/*
                ReactTabulator component with dynamic multi-level grouping
                - key combines both grouping fields to force recreation when either changes
                - events prop handles group interactions
            */}
            <div style={{ 
                flex: 1, 
                overflow: 'hidden',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <ReactTabulator
                    key={`${groupField}-${secondaryGroupField}-${transformedData.length}`} // Force recreation on any change
                    data={transformedData}
                    columns={columns}
                    options={options}
                    tableRef={tableRef}
                    events={{
                        groupClick: handleGroupClick,
                        groupToggle: handleGroupToggle,
                    }}
                />
            </div>

            {/* Styles for collapsed row formatting and scrollbar fixes */}
            <style>{`
                .ts-collapse { 
                    width: 100%; 
                    border-collapse: collapse; 
                    font-size: 0.875rem; 
                    margin: 8px 0;
                }
                .ts-collapse th { 
                    text-align: left; 
                    padding: 6px 8px; 
                    font-weight: 600; 
                    width: 35%; 
                    border-bottom: 1px solid #eee; 
                    white-space: nowrap; 
                    background-color: #f8f9fa;
                }
                .ts-collapse td { 
                    padding: 6px 8px; 
                    border-bottom: 1px solid #eee; 
                    word-break: break-word; 
                }
                .tabulator-responsive-collapse { 
                    padding: 8px 12px; 
                    background-color: #fafafa;
                }
                .tabulator {
                    height: 100% !important;
                }
                /* Specific fixes for grouped tables */
                .tabulator[data-grouped="true"] {
                    overflow: hidden !important;
                }
                .tabulator[data-grouped="true"] .tabulator-tableHolder {
                    overflow: auto !important;
                    max-height: 350px !important;
                }
                /* Ensure group headers don't interfere with scrolling */
                .tabulator-group {
                    background-color: #e9ecef;
                    font-weight: 600;
                    position: sticky !important;
                    top: 0 !important;
                    z-index: 10 !important;
                }
                .tabulator-group:hover {
                    background-color: #dee2e6;
                }
                /* Fix group row positioning */
                .tabulator-group-level-0 {
                    position: relative !important;
                }
                .tabulator-table {
                    font-size: 14px;
                }
            `}</style>
        </div>
    );
}